import { Injectable } from '@angular/core';
import { concat, Observable, of } from 'rxjs';
import { distinctUntilChanged, first, map, timeout } from 'rxjs/operators';

import { AnalyticsService } from './analytics.service';

/**
 * Feature flag de PostHog que enciende o apaga el módulo de Fina Pago completo en Fina Web: la promo
 * y la píldora de la topbar, el banner y la insignia de Cuentas bancarias, el modal de activación, los
 * tiles y filas de Fina Pago en el POS y la opción «Fina Pago» al cobrar cuentas por cobrar.
 *
 * Hay que crearlo en los DOS proyectos de PostHog (staging/local y producción); si no existe,
 * `getExperimentVariant` devuelve false y el módulo queda oculto en todos los ambientes (fail-closed,
 * sin excepción para local). Target por grupo `tenant` = tenantId.
 *
 * Solo de frontend, igual que `cotizacionesv2` y `nina_flag`: el API no tiene SDK de PostHog y los
 * endpoints `/api/payments` siguen protegidos por permisos. Los cobros ya hechos (historial de la
 * cuenta, detalle de venta) se siguen mostrando con el flag apagado: son registros, no acciones.
 */
export const FINA_PAGO_FLAG = 'finapago';

/** Tope de espera hasta que carguen los flags de PostHog (~1.5 s). */
const FLAG_WAIT_MS = 3000;

@Injectable({ providedIn: 'root' })
export class FinaPagoAccessService {
    constructor(private readonly analyticsService: AnalyticsService) {}

    /** Lectura síncrona: para `*ngIf`, getters y defensas de handler. */
    canAccessSync(): boolean {
        return this.analyticsService.getExperimentVariant(FINA_PAGO_FLAG);
    }

    /** Valor actual del flag y cada cambio posterior (PostHog los entrega hasta ~1.5 s después del init). */
    canAccessChanges$(): Observable<boolean> {
        return concat(of(null), this.analyticsService.flagsUpdated$).pipe(
            map(() => this.canAccessSync()),
            distinctUntilChanged(),
        );
    }

    /** `true` apenas el flag se encienda; si no se enciende en `FLAG_WAIT_MS`, el valor actual (fail-closed). */
    canAccess$(): Observable<boolean> {
        return this.canAccessChanges$().pipe(first(Boolean), timeout({ first: FLAG_WAIT_MS, with: () => of(this.canAccessSync()) }));
    }
}
